import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { PaymentRequest, PaymentRequestEvent } from '@stripe/stripe-js';
import { useEffect, useRef, useState } from 'react';
import { microcopySet, useMicrocopy } from '../../../../utils/contentfulMicrocopy';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import Typography from '../../text/Typography';

const OtherOptions = styled.div`
  display: flex;
  align-items: center;
  margin: ${spacing[1]}px 0 ${spacing[4]}px;
  ${breakpoints.desktop} {
    margin-bottom: ${spacing[5]}px;
  }
`;

const Line = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${colors.gray};
`;

export default function StripePaymentRequestButton({
  amount,
  currencyCode,
  setPaymentRequestEnabled,
  onPaymentMethodSelected,
  onUpdateAvailableWallets,
}: {
  amount?: number;
  currencyCode: string;
  setPaymentRequestEnabled: (paymentRequestEnabled: boolean) => void;
  onUpdateAvailableWallets: (wallet: string) => void;
  onPaymentMethodSelected: (
    paymentMethod: string,
    type: PaymentRequestEvent['walletName'],
    obj: { email: string; firstName?: string; lastName?: string },
  ) => void;
}) {
  const t = useMicrocopy();
  const stripe = useStripe();
  const [paymentRequestObject, setPaymentRequestObject] = useState<PaymentRequest>();
  const manualPaymentOptionLabel = t(microcopySet.PAYMENT_OPTION, 'manualPaymentOptionLabel');
  const proceedPaymentLink = useRef<
    (
      paymentMethod: string,
      type: string,
      billingDetails: {
        email: string;
        firstName?: string | undefined;
        lastName?: string | undefined;
      },
    ) => void
  >();

  proceedPaymentLink.current = onPaymentMethodSelected;

  useEffect(() => {
    if (!stripe) return;
    if (amount === undefined || isNaN(amount)) return;

    const paymentRequest = stripe.paymentRequest({
      country: 'SE',
      currency: currencyCode.toLowerCase(),
      total: {
        label: 'Milkywire support',
        amount: amount * 100,
      },
      requestPayerEmail: true,
      requestPayerName: true,
    });

    paymentRequest.on('paymentmethod', async (paymentMethodEvent) => {
      const {
        billing_details: { email, name },
      } = paymentMethodEvent.paymentMethod;
      if (!email || !name) {
        return paymentMethodEvent.complete('fail');
      }
      if (proceedPaymentLink?.current) {
        onUpdateAvailableWallets(paymentMethodEvent.walletName);
        proceedPaymentLink.current(
          paymentMethodEvent.paymentMethod.id,
          paymentMethodEvent.walletName,
          {
            email,
            firstName: name.split(' ')[0],
            lastName: name.split(' ').slice(1).join(' '),
          },
        );
      }
      return paymentMethodEvent.complete('success');
    });

    // Check the availability of the Payment Request API first.
    paymentRequest.canMakePayment().then(function (result) {
      if (result) {
        if (result.applePay) {
          onUpdateAvailableWallets('applePay');
        } else if (result.googlePay) {
          onUpdateAvailableWallets('googlePay');
        }
        setPaymentRequestObject(paymentRequest);
        setPaymentRequestEnabled(true);
      } else {
        setPaymentRequestEnabled(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  useEffect(() => {
    if (paymentRequestObject) {
      paymentRequestObject?.update({
        currency: currencyCode.toLowerCase(),
        total: {
          label: 'Milkywire support',
          amount: (amount || 0) * 100,
        },
      });
    }
  }, [amount, currencyCode, paymentRequestObject]);

  return (
    <>
      {!!paymentRequestObject && (
        <>
          <PaymentRequestButtonElement
            options={{
              paymentRequest: paymentRequestObject,
              style: { paymentRequestButton: { theme: 'dark', type: 'donate', height: '40px' } },
            }}
          />
          <OtherOptions>
            <Line />
            <Typography
              variant="detail"
              css={css`
                margin: 0 10px;
              `}
              color={colors.gray}
            >
              {manualPaymentOptionLabel}
            </Typography>
            <Line />
          </OtherOptions>
        </>
      )}
    </>
  );
}
